import type { MouseEvent } from 'react';
import { Btn } from '../primitives/Btn';
import { BtnOutline } from '../primitives/BtnOutline';
import { FadeUp } from '../primitives/FadeUp';
import { SectionH2 } from '../primitives/SectionH2';
import { SectionLabel } from '../primitives/SectionLabel';
import { Check } from '../icons';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  ACCENT,
  BORDER,
  EASE,
  FONT_DISPLAY,
  FONT_MONO,
  MUTED,
  SURFACE2,
  SURFACE_LIGHT,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from '../../tokens';

interface Tier {
  stage: string;
  name: string;
  /** One-line summary of who this tier is for. */
  sub: string;
  desc: string;
  /** Everything the tier includes, in the order it gets delivered. */
  includes: readonly string[];
  /** The recommended tier, which gets the badge, accent border and filled CTA. */
  highlight: boolean;
  cta: string;
}

const TIERS: readonly Tier[] = [
  {
    stage: 'Entry',
    name: 'Momentum Engine',
    sub: 'For high ad spend and low conversion',
    desc: 'Two agents on the two places most businesses lose the most, the moment a lead comes in and the moment a job finishes.',
    includes: [
      'Speed-to-Lead Agent, fires on any ad or web form submit',
      'Two-touch qualifying conversation, then a booking link',
      'Reputation Agent, fires on service completion',
      'Review requests on each customer preferred channel',
      'Integration with your existing forms and calendar',
      'Both agents configured against your offers and pricing',
    ],
    highlight: false,
    cta: 'Start Here Instead →',
  },
  {
    stage: 'Core',
    name: 'Content OS',
    sub: 'The full system, content through to ads',
    desc: 'Everything below is included in one retainer. Content, copywriting and production carry no revenue share. Only Meta ad spend does.',
    includes: [
      '5x per week LinkedIn, text and image',
      '5x per week Instagram and Facebook, carousels plus footage from the job',
      'EGC direction, phone footage only, or a fractional creator if nobody wants to be on camera',
      '43 pieces published every month, all human-checked',
      'Meta ads, built and managed, creative cut from what already landed',
      'Competitor Scan, what is landing for them and what is not',
      'Objection Miner across your own calls and email chains',
      'Objection Scriptwriter, drafting in your documented voice',
      'Directive Files, your voice, offers, pricing, ICP and objections',
      'Weekly performance report and the learning loop behind it',
      'Content OS dashboard, and I work with your team directly',
      'Month-to-month, no lock-in, 30 days notice to cancel',
      'Files, history and reports are yours if you leave',
    ],
    highlight: true,
    cta: 'Get My Free Competitor Scan →',
  },
  {
    stage: 'Later',
    name: 'Performance Layer',
    sub: 'Once the front end is already working',
    desc: 'The two-hour week applied past marketing and into the rest of the operation. This is a later conversation, not a first one.',
    includes: [
      'Technical execution moved inside your own tools',
      'Operational data reporting across the business',
      'Objection mining extended beyond sales calls',
      'Systems installed in your stack, owned by your team',
      'Scoped to your business after Content OS has proven out',
    ],
    highlight: false,
    cta: 'Ask About This Later →',
  },
];

interface PricingSectionProps {
  ctaHref: string;
  onCta: (event: MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * The full extent of the client-side ask. Sits above the guarantee because the
 * "how much work is this for me" objection bites hardest right before the CTA.
 */
const WHAT_I_NEED: readonly { title: string; body: string }[] = [
  {
    title: 'Photos and clips from the job',
    body: 'Pull your phone out while you are working and film what you are already doing. The more of an ordinary day you catch, the more I have to build with. Anyone on the team can shoot it.',
  },
  {
    title: 'Your face, only where it counts',
    body: 'For the pieces where your credibility is doing the selling, you talk to your phone for two minutes. No lighting, no crew, no script. Raw outperforms produced.',
  },
  {
    title: 'A yes or a no on the queue',
    body: 'Everything else is handled. Writing, scheduling, resizing, review requests and follow-ups are done before you look at them. You approve what is queued.',
  },
];

export function PricingSection({ ctaHref, onCta }: PricingSectionProps) {
  const isMobile = useIsMobile();

  return (
    <section
      id="included"
      style={{ padding: isMobile ? '60px 20px' : '130px 64px', background: 'rgba(8,8,8,0.88)' }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <FadeUp>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <SectionLabel number="07">What&rsquo;s Included</SectionLabel>
            <SectionH2 style={{ marginBottom: 16 }}>Everything in one retainer. Nothing billed line by line.</SectionH2>
            <p style={{ color: TEXT_SECONDARY, maxWidth: 560, margin: '0 auto', lineHeight: 1.75 }}>
              Pricing is a conversation we have after the free competitor scan, once you have seen the
              gap and we both know which tier actually fits. Here is exactly what sits inside each one.
            </p>
          </div>
        </FadeUp>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)',
            gap: 20,
            marginBottom: 36,
            alignItems: 'start',
          }}
        >
          {TIERS.map((tier, i) => (
            <FadeUp key={tier.name} delay={i * 80}>
              <div
                style={{
                  background: tier.highlight ? SURFACE2 : SURFACE_LIGHT,
                  border: `1px solid ${tier.highlight ? 'rgba(255,107,0,0.4)' : BORDER}`,
                  borderRadius: 20,
                  padding: '36px 30px',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: tier.highlight ? '0 0 60px rgba(255,107,0,0.08)' : 'none',
                  transition: `transform 0.3s ${EASE}, box-shadow 0.3s`,
                  height: '100%',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = tier.highlight
                    ? '0 12px 60px rgba(255,107,0,0.15)'
                    : '0 12px 40px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = tier.highlight
                    ? '0 0 60px rgba(255,107,0,0.08)'
                    : 'none';
                }}
              >
                {tier.highlight && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -14,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: ACCENT,
                      color: SURFACE2,
                      fontFamily: FONT_MONO,
                      fontSize: '0.65rem',
                      letterSpacing: '0.1em',
                      padding: '5px 16px',
                      borderRadius: 100,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    START HERE
                  </div>
                )}

                <div style={{ fontFamily: FONT_MONO, fontSize: '0.65rem', color: MUTED, letterSpacing: '0.08em', marginBottom: 10 }}>
                  {tier.stage}
                </div>

                <div
                  style={{
                    fontFamily: FONT_DISPLAY,
                    fontSize: '1.9rem',
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    color: tier.highlight ? ACCENT : TEXT_PRIMARY,
                    marginBottom: 6,
                    lineHeight: 1.05,
                  }}
                >
                  {tier.name}
                </div>

                <div style={{ fontFamily: FONT_MONO, fontSize: '0.65rem', color: MUTED, letterSpacing: '0.06em', marginBottom: 18 }}>
                  {tier.sub}
                </div>

                <p style={{ color: TEXT_SECONDARY, fontSize: '0.88rem', lineHeight: 1.65, marginBottom: 22 }}>
                  {tier.desc}
                </p>

                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10, flex: 1 }}>
                  {tier.includes.map((item) => (
                    <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <span style={{ color: ACCENT, flexShrink: 0, marginTop: 2 }}>
                        <Check size={14} strokeWidth={3} />
                      </span>
                      <span style={{ fontSize: '0.85rem', color: TEXT_SECONDARY, lineHeight: 1.5 }}>{item}</span>
                    </li>
                  ))}
                </ul>

                <div style={{ marginTop: 28 }}>
                  {tier.highlight ? (
                    <Btn href={ctaHref} onClick={onCta} style={{ width: '100%', justifyContent: 'center' }}>
                      {tier.cta}
                    </Btn>
                  ) : (
                    <BtnOutline href={ctaHref} onClick={onCta} style={{ width: '100%', justifyContent: 'center' }}>
                      {tier.cta}
                    </BtnOutline>
                  )}
                </div>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={110}>
          <div
            style={{
              background: SURFACE2,
              border: `1px solid ${BORDER}`,
              borderRadius: 20,
              padding: isMobile ? '28px 24px' : '32px 36px',
              marginBottom: 20,
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: '0.68rem',
                  color: ACCENT,
                  letterSpacing: '0.1em',
                  marginBottom: 10,
                }}
              >
                WHAT I NEED FROM YOU
              </div>
              <p style={{ color: TEXT_SECONDARY, fontSize: '0.95rem', lineHeight: 1.7, maxWidth: 560, margin: '0 auto' }}>
                This is the whole list. Around 80% of it is just filming yourself working on a phone,
                which is also the version that performs best.
              </p>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: isMobile ? 20 : 28,
              }}
            >
              {WHAT_I_NEED.map((need, i) => (
                <div key={need.title} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span
                    style={{
                      fontFamily: FONT_MONO,
                      fontSize: '0.68rem',
                      color: ACCENT,
                      flexShrink: 0,
                      marginTop: 3,
                    }}
                  >
                    0{i + 1}
                  </span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: TEXT_PRIMARY, marginBottom: 6 }}>
                      {need.title}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: MUTED, lineHeight: 1.6 }}>{need.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={120}>
          <div
            style={{
              background: SURFACE2,
              border: '1px solid rgba(255,107,0,0.2)',
              borderRadius: 20,
              padding: '28px 32px',
              textAlign: 'center',
              marginBottom: 20,
            }}
          >
            <div style={{ fontFamily: FONT_MONO, fontSize: '0.68rem', color: ACCENT, letterSpacing: '0.1em', marginBottom: 10 }}>
              THE GUARANTEE
            </div>
            <p style={{ color: TEXT_SECONDARY, fontSize: '0.95rem', lineHeight: 1.7, maxWidth: 600, margin: '0 auto' }}>
              No lock-in contract. Month-to-month, cancel anytime. Your content system live in 14 days,
              and 20 pieces of content published every month, guaranteed, or that month&rsquo;s on me.
            </p>
          </div>
        </FadeUp>

        <FadeUp delay={160}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: MUTED, fontSize: '0.82rem', lineHeight: 1.6, maxWidth: 520, margin: '0 auto' }}>
              <span style={{ color: ACCENT }}>✦</span> Your business info only, not client data. Deleted
              on request. Yours if you leave. <span style={{ color: ACCENT }}>✦</span>
            </p>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
